/* eslint-disable react/no-multi-comp -- техдолг: вспомогательные компоненты вынести в отдельные файлы (docs/todos/tech-debt/admin-page-helper-components.md) */
import type { FormEvent, ReactElement } from 'react';
import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { useListAuditLogQuery } from '@/api/auditLogApi';
import { Input } from '@/components/Input';
import type { AuditLogItem } from '@/lib/auditLogSchemas';

const PAGE_SIZE = 20;

function formatDateTime(iso: string): string {
    return new Date(iso).toLocaleString('ru-RU');
}

function formatAdmin(item: AuditLogItem): string {
    if (item.admin === null) {
        return item.adminId.slice(0, 8);
    }
    if (item.admin.email !== '') {
        return item.admin.email;
    }
    return item.adminId.slice(0, 8);
}

function formatTarget(item: AuditLogItem): string {
    if (item.targetType === null && item.targetId === null) {
        return '—';
    }
    const type = item.targetType ?? '';
    const id = item.targetId !== null ? item.targetId.slice(0, 8) : '';
    if (type === '' && id === '') {
        return '—';
    }
    if (id === '') {
        return type;
    }
    return `${type}:${id}`;
}

interface PayloadBlockProps {
    payload: Record<string, unknown> | null;
    rowId: string;
}

function PayloadBlock({ payload, rowId }: PayloadBlockProps): ReactElement {
    const [isOpen, setOpen] = useState(false);

    if (payload === null) {
        return <span className="text-xs text-gray-400">—</span>;
    }

    return (
        <div className="w-full" data-testid={`audit-log-payload-${rowId}`}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid={`audit-log-payload-toggle-${rowId}`}
                className="inline-flex items-center gap-1 text-xs text-gray-700 underline transition hover:text-gray-900"
            >
                {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                {isOpen ? 'Скрыть' : 'Показать'}
            </button>
            {isOpen && (
                <pre
                    className="mt-1 max-h-64 overflow-auto rounded border border-gray-200 bg-gray-50 p-2 text-xs text-gray-800"
                    data-testid={`audit-log-payload-json-${rowId}`}
                >
                    {JSON.stringify(payload, null, 2)}
                </pre>
            )}
        </div>
    );
}

export function AuditLogPage(): ReactElement {
    const [filterAdminId, setFilterAdminId] = useState('');
    const [filterAction, setFilterAction] = useState('');
    const [filterTargetType, setFilterTargetType] = useState('');
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');

    const [appliedAdminId, setAppliedAdminId] = useState('');
    const [appliedAction, setAppliedAction] = useState('');
    const [appliedTargetType, setAppliedTargetType] = useState('');
    const [appliedDateFrom, setAppliedDateFrom] = useState('');
    const [appliedDateTo, setAppliedDateTo] = useState('');

    const [page, setPage] = useState(0);

    const queryArg = useMemo(
        () => ({
            ...(appliedAdminId !== '' ? { adminId: appliedAdminId } : {}),
            ...(appliedAction !== '' ? { action: appliedAction } : {}),
            ...(appliedTargetType !== '' ? { targetType: appliedTargetType } : {}),
            ...(appliedDateFrom !== '' ? { dateFrom: appliedDateFrom } : {}),
            ...(appliedDateTo !== '' ? { dateTo: appliedDateTo } : {}),
            limit: PAGE_SIZE,
            offset: page * PAGE_SIZE
        }),
        [appliedAdminId, appliedAction, appliedTargetType, appliedDateFrom, appliedDateTo, page]
    );

    const { data, isLoading, isError } = useListAuditLogQuery(queryArg);

    const items = data?.items ?? [];
    const total = data?.total ?? 0;
    const totalPages = total === 0 ? 0 : Math.ceil(total / PAGE_SIZE);

    const handleApplyFilters = (e: FormEvent<HTMLFormElement>): void => {
        e.preventDefault();
        setAppliedAdminId(filterAdminId.trim());
        setAppliedAction(filterAction.trim());
        setAppliedTargetType(filterTargetType.trim());
        setAppliedDateFrom(filterDateFrom !== '' ? new Date(filterDateFrom).toISOString() : '');
        setAppliedDateTo(filterDateTo !== '' ? new Date(filterDateTo).toISOString() : '');
        setPage(0);
    };

    const handleResetFilters = (): void => {
        setFilterAdminId('');
        setFilterAction('');
        setFilterTargetType('');
        setFilterDateFrom('');
        setFilterDateTo('');
        setAppliedAdminId('');
        setAppliedAction('');
        setAppliedTargetType('');
        setAppliedDateFrom('');
        setAppliedDateTo('');
        setPage(0);
    };

    return (
        <div data-testid="audit-log-page" className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold text-gray-900">Журнал действий</h1>
            </div>

            <form
                onSubmit={handleApplyFilters}
                className="grid grid-cols-1 gap-3 rounded border border-gray-200 bg-white p-3 sm:grid-cols-2 lg:grid-cols-5"
                data-testid="audit-log-filters"
            >
                <Input
                    label="Admin ID"
                    value={filterAdminId}
                    onChange={(e) => setFilterAdminId(e.target.value)}
                    placeholder="UUID"
                    data-testid="audit-log-filter-admin-id"
                />
                <Input
                    label="Action"
                    value={filterAction}
                    onChange={(e) => setFilterAction(e.target.value)}
                    placeholder="subscription.cancel"
                    data-testid="audit-log-filter-action"
                />
                <Input
                    label="Target type"
                    value={filterTargetType}
                    onChange={(e) => setFilterTargetType(e.target.value)}
                    placeholder="subscription"
                    data-testid="audit-log-filter-target-type"
                />
                <Input
                    label="Дата с"
                    type="datetime-local"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    data-testid="audit-log-filter-date-from"
                />
                <Input
                    label="Дата по"
                    type="datetime-local"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    data-testid="audit-log-filter-date-to"
                />
                <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-5">
                    <button
                        type="submit"
                        className="rounded bg-gray-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
                        data-testid="audit-log-filter-submit"
                    >
                        Применить
                    </button>
                    <button
                        type="button"
                        onClick={handleResetFilters}
                        className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 transition hover:bg-gray-100"
                        data-testid="audit-log-filter-reset"
                    >
                        Сбросить
                    </button>
                </div>
            </form>

            {isLoading && <div className="text-sm text-gray-500">Загрузка...</div>}
            {isError && (
                <div className="text-sm text-red-600" data-testid="audit-log-error">
                    Не удалось загрузить журнал
                </div>
            )}

            {!isLoading && !isError && (
                <div
                    className="overflow-hidden rounded border border-gray-200 bg-white"
                    data-testid="audit-log-table"
                >
                    <div className="grid grid-cols-[160px_minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1.2fr)_minmax(0,2fr)] gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium uppercase text-gray-600">
                        <span>Когда</span>
                        <span>Админ</span>
                        <span>Действие</span>
                        <span>Цель</span>
                        <span>Payload</span>
                    </div>

                    {items.length === 0 && (
                        <div className="px-3 py-6 text-center text-sm text-gray-500">
                            Записи не найдены
                        </div>
                    )}

                    {items.map((item) => (
                        <div
                            key={item.id}
                            className="grid grid-cols-[160px_minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1.2fr)_minmax(0,2fr)] items-start gap-2 border-b border-gray-100 px-3 py-2 text-sm last:border-b-0"
                            data-testid={`audit-log-row-${item.id}`}
                        >
                            <span className="text-xs text-gray-700">
                                {formatDateTime(item.createdAt)}
                            </span>
                            <span className="truncate text-gray-900">{formatAdmin(item)}</span>
                            <span className="truncate font-mono text-xs text-gray-800">
                                {item.action}
                            </span>
                            <span className="truncate font-mono text-xs text-gray-700">
                                {formatTarget(item)}
                            </span>
                            <PayloadBlock payload={item.payload} rowId={item.id} />
                        </div>
                    ))}
                </div>
            )}

            {totalPages > 1 && (
                <div
                    className="flex items-center justify-between text-sm text-gray-700"
                    data-testid="audit-log-pagination"
                >
                    <span>
                        Страница {page + 1} из {totalPages} · всего {total}
                    </span>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={page === 0}
                            data-testid="audit-log-page-prev"
                            className="rounded border border-gray-300 px-3 py-1 text-xs transition hover:bg-gray-100 disabled:opacity-50"
                        >
                            Назад
                        </button>
                        <button
                            type="button"
                            onClick={() => setPage((p) => p + 1)}
                            disabled={page + 1 >= totalPages}
                            data-testid="audit-log-page-next"
                            className="rounded border border-gray-300 px-3 py-1 text-xs transition hover:bg-gray-100 disabled:opacity-50"
                        >
                            Вперёд
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
